import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import interviewImage from "../assets/images/interview.webp";

const CHARCOAL = "#111111";
const GOLD = "#C4A25D";
const WARM_WHITE = "#F5F0E8";

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut", delay: 0.15 } },
};

export default function HomeAbout() {
  return (
    <Wrapper>
      <Inner>
        <ImageSide
          as={motion.div}
          variants={fadeLeft}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <ImageFrame>
            <img src={interviewImage} alt="Jim Montour in interview" loading="lazy" />
          </ImageFrame>
          <AccentLine />
        </ImageSide>

        <ContentSide
          as={motion.div}
          variants={fadeRight}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <Overline>About Me</Overline>
          <Heading>
            Good communication<br />
            is built, not improvised.
          </Heading>
          <Body>
            I'm Jim Montour — a PR professional and consultant based in Buffalo, NY.
            I've spent 15+ years working inside government, nonprofits, and advocacy
            organizations — not just writing press releases, but helping teams figure
            out what they're actually trying to say and how to say it consistently.
          </Body>
          <Body>
            These days I work as a PR Specialist at E2CCB, take on fractional
            consulting work with mission-driven organizations, and help teams
            build better processes around how they communicate.
          </Body>
          <LearnMore to="/about">
            Learn More <Arrow>→</Arrow>
          </LearnMore>
        </ContentSide>
      </Inner>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  width: 100%;
  background: ${CHARCOAL};
  padding: 5rem 2rem;

  @media (max-width: 600px) {
    padding: 3.5rem 1.25rem;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  gap: 4rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ImageSide = styled.div`
  flex: 1 1 280px;
  position: relative;
`;

const ImageFrame = styled.div`
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    display: block;
    object-fit: cover;
    transition: transform 0.6s ease;
    filter: grayscale(15%);
  }

  &:hover img {
    transform: scale(1.03);
    filter: grayscale(0%);
  }

  /* Gold corner accent */
  &::before {
    content: "";
    position: absolute;
    top: -10px;
    left: -10px;
    width: 60px;
    height: 60px;
    border-top: 2px solid ${GOLD};
    border-left: 2px solid ${GOLD};
    z-index: 1;
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    right: -10px;
    width: 60px;
    height: 60px;
    border-bottom: 2px solid ${GOLD};
    border-right: 2px solid ${GOLD};
    z-index: 1;
    pointer-events: none;
  }
`;

const AccentLine = styled.div`
  position: absolute;
  bottom: -2rem;
  left: 1rem;
  width: 40px;
  height: 2px;
  background: ${GOLD};
`;

const ContentSide = styled.div`
  flex: 1 1 280px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Overline = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${GOLD};
`;

const Heading = styled.h2`
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: clamp(1.75rem, 3vw, 2.4rem);
  color: ${WARM_WHITE};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Body = styled.p`
  font-size: 0.9375rem;
  color: #9E9E8E;
  line-height: 1.8;
  margin: 0;
`;

const LearnMore = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${GOLD};
  text-decoration: none;
  margin-top: 0.5rem;
  transition: gap 0.2s;

  &:hover {
    gap: 0.7rem;
    color: ${GOLD};
  }
`;

const Arrow = styled.span`
  transition: transform 0.2s;
  ${LearnMore}:hover & {
    transform: translateX(3px);
  }
`;
